// Tasks follow the CallSync app: the same types, quick times, reminders and overdue rule, so a
// task reads the same on the dashboard and on the employee's phone. Due date and time have no
// time zone (crm_tasks.due_date is a date, due_time a time), exactly as the phone stores them;
// "today" and "now" are the viewer's local clock, like every other date filter here.

/** The app's task types, in the order the app offers them. */
export const TASK_TYPES = ['FOLLOW_UP', 'MEETING', 'CALL_BACK', 'SEND_DETAILS', 'PAYMENT', 'TO_DO', 'OTHER'] as const;
export type AppTaskType = (typeof TASK_TYPES)[number];
/** Older Sales and MCP tasks can also be CALL or EMAIL. */
export type TaskType = AppTaskType | 'CALL' | 'EMAIL';
export type TaskStatus = 'PENDING' | 'COMPLETED' | 'CANCELLED';

const TYPE_LABELS: Record<AppTaskType, string> = {
  FOLLOW_UP: 'Follow up',
  MEETING: 'Meeting',
  CALL_BACK: 'Call back',
  SEND_DETAILS: 'Send details',
  PAYMENT: 'Payment',
  TO_DO: 'To do',
  OTHER: 'Other',
};
// The app shows the older types as one of its own and keeps the stored value.
const SHOWN_AS: Record<string, AppTaskType> = { CALL: 'CALL_BACK', EMAIL: 'SEND_DETAILS' };

/** The app type a stored type shows as. */
export const shownType = (type: string): AppTaskType =>
  SHOWN_AS[type] ?? ((TASK_TYPES as readonly string[]).includes(type) ? (type as AppTaskType) : 'OTHER');
export const taskTypeLabel = (type: string) => TYPE_LABELS[shownType(type)];

/** Stored types a type covers: the app type and the older ones that show as it. */
export const storedTypes = (type: AppTaskType): TaskType[] => [
  type,
  ...(Object.keys(SHOWN_AS) as TaskType[]).filter((stored) => SHOWN_AS[stored] === type),
];

/**
 * Tabs on the Tasks page: the app's own groups (Today, Overdue, Upcoming), Completed, and under
 * "More" the other open lists, one per type, tasks removed on the phone and everything.
 */
export type TaskTab =
  | 'today'
  | 'overdue'
  | 'upcoming'
  | 'completed'
  | 'open'
  | 'unassigned'
  | 'nodate'
  | 'removed'
  | 'all'
  | `type:${AppTaskType}`;

const PLAIN_TABS: string[] = ['today', 'overdue', 'upcoming', 'completed', 'open', 'unassigned', 'nodate', 'removed', 'all'];
// Links from before the tabs followed the app.
const OLD_TABS: Record<string, TaskTab> = { todo: 'open', done: 'completed' };

export const typeTab = (type: AppTaskType): TaskTab => `type:${type}`;

/** The app type a type tab lists, or null. */
export function tabType(tab: TaskTab): AppTaskType | null {
  const type = tab.startsWith('type:') ? tab.slice(5) : '';
  return (TASK_TYPES as readonly string[]).includes(type) ? (type as AppTaskType) : null;
}

/** A tab from the address bar; anything unknown opens Today. */
export function parseTaskTab(value: string): TaskTab {
  if (PLAIN_TABS.includes(value)) return value as TaskTab;
  if (OLD_TABS[value]) return OLD_TABS[value];
  const tab = value as TaskTab;
  return tabType(tab) ? tab : 'today';
}

/** The status a tab lists; null for every status. */
export function tabStatus(tab: TaskTab): TaskStatus | null {
  if (tab === 'completed') return 'COMPLETED';
  if (tab === 'removed') return 'CANCELLED';
  if (tab === 'all') return null;
  return 'PENDING';
}

/** The viewer's local day (yyyy-MM-dd) and time (HH:mm). */
export interface Clock {
  today: string;
  time: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

export function clockAt(now = new Date()): Clock {
  return {
    today: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
  };
}

export function addDays(day: string, days: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** HH:mm from a database time ("14:00:00"), or null. */
export function normalizeTime(value: unknown): string | null {
  return typeof value === 'string' && /^\d{2}:\d{2}/.test(value) ? value.slice(0, 5) : null;
}

/**
 * As on the phone: a task is overdue once its due time has passed, or, without a time, once its
 * due day is over. Only open tasks are overdue.
 */
export function isOverdue(
  task: { status: TaskStatus; dueDate: string | null; dueTime: string | null },
  clock: Clock,
): boolean {
  if (task.status !== 'PENDING' || !task.dueDate) return false;
  if (task.dueDate < clock.today) return true;
  const time = normalizeTime(task.dueTime);
  return task.dueDate === clock.today && time !== null && time <= clock.time;
}

/** The same rule as a PostgREST filter group; combine it with status=PENDING. */
export const overdueGroup = (clock: Clock) =>
  `or(due_date.lt.${clock.today},and(due_date.eq.${clock.today},due_time.lte.${clock.time}))`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Today", "Tomorrow", "Yesterday", "12 Nov" or "12 Nov 2027". */
export function dayLabel(day: string, clock: Clock): string {
  if (day === clock.today) return 'Today';
  if (day === addDays(clock.today, 1)) return 'Tomorrow';
  if (day === addDays(clock.today, -1)) return 'Yesterday';
  const [y, m, d] = day.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}${y === Number(clock.today.slice(0, 4)) ? '' : ` ${y}`}`;
}

/** "2:00 PM", as the dashboard formats times. */
export function timeLabel(time: string): string {
  const [h, m] = time.split(':').map(Number);
  return `${h % 12 || 12}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`;
}

export function dueLabel(dueDate: string | null, dueTime: string | null, clock: Clock): string {
  if (!dueDate) return 'No date';
  const time = normalizeTime(dueTime);
  return time ? `${dayLabel(dueDate, clock)}, ${timeLabel(time)}` : dayLabel(dueDate, clock);
}

/** The app's quick due days. */
export function dateChoices(clock: Clock): { value: string; label: string }[] {
  return [
    { value: clock.today, label: 'Today' },
    { value: addDays(clock.today, 1), label: 'Tomorrow' },
    { value: addDays(clock.today, 2), label: 'Day after' },
  ];
}

/** The app's fixed times; ones already past are left out for a task due today. */
export const TIME_CHOICES = [
  { value: '11:00', label: '11 AM' },
  { value: '14:00', label: '2 PM' },
  { value: '17:00', label: '5 PM' },
];
export const timeChoicesFor = (dueDate: string | null, clock: Clock) =>
  TIME_CHOICES.filter((choice) => dueDate !== clock.today || choice.value > clock.time);

/** A reminder as minutes before the due time ("0" is on time), or "none". */
export type ReminderValue = string;

export function reminderLabel(showReminder: boolean, minutesBefore: number): string | null {
  if (!showReminder) return null;
  if (minutesBefore <= 0) return 'On time';
  if (minutesBefore >= 60 && minutesBefore % 60 === 0) {
    const hours = minutesBefore / 60;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} before`;
  }
  return `${minutesBefore} min before`;
}

export const reminderValue = (showReminder: boolean, minutesBefore: number): ReminderValue =>
  showReminder ? String(Math.max(0, minutesBefore)) : 'none';

/**
 * The app's order: on time, 30 min before, no reminder. A task from elsewhere keeps its own
 * reminder, listed before No reminder.
 */
export function reminderChoices(current: ReminderValue): { value: ReminderValue; label: string }[] {
  const choices = [
    { value: '0', label: 'On time' },
    { value: '30', label: '30 min before' },
  ];
  const none = { value: 'none', label: 'No reminder' };
  if (current === 'none' || choices.some((choice) => choice.value === current)) return [...choices, none];
  return [...choices, { value: current, label: reminderLabel(true, Number(current)) ?? current }, none];
}

/** A reminder needs a due date and time, as on the phone. */
export function reminderFields(value: ReminderValue, dueDate: string | null, dueTime: string | null) {
  const minutes = Number(value);
  return value === 'none' || !dueDate || !dueTime || !Number.isFinite(minutes)
    ? { showReminder: false, reminderMinutesBefore: 0 }
    : { showReminder: true, reminderMinutesBefore: Math.max(0, Math.round(minutes)) };
}
